/**
 * Shared blob-download plumbing for generated documents. One place owns the
 * export-styles query (with its offline fallback) and the object-URL/anchor
 * dance, so ExportControls and the doc-generator result view can't drift apart.
 */
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { errorMessage, useToast } from '@/components/Toast';

export type ExportFormat = 'PDF' | 'DOCX' | 'MARKDOWN';
export type ExportPart = 'DOCUMENT' | 'GUIDE' | 'CHECKLIST' | 'ALL';

/** Style packs offered while the export-styles query is loading or failed. */
export const FALLBACK_EXPORT_STYLES = ['court-filing', 'contract', 'plain'];

/** The export style packs from the backend, falling back to the built-in trio. */
export function useExportStyles(): string[] {
  const styles = useQuery({ queryKey: ['export-styles'], queryFn: api.docgen.exportStyles });
  return styles.data ?? FALLBACK_EXPORT_STYLES;
}

/** Save a blob through a transient object-URL + anchor click. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Download a rendered document part via the export endpoint, naming the file
 * from the server's Content-Disposition. Failures surface as a danger toast;
 * `busy` drives the button's loading state.
 */
export function useDocumentDownload(): {
  busy: boolean;
  download: (documentId: string, format: ExportFormat, part: ExportPart, style: string) => Promise<void>;
} {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const download = useCallback(
    async (documentId: string, format: ExportFormat, part: ExportPart, style: string) => {
      setBusy(true);
      try {
        const { blob, filename } = await api.docgen.export(documentId, format, part, style);
        saveBlob(blob, filename);
      } catch (e) {
        toast.push(errorMessage(e), 'danger');
      } finally {
        setBusy(false);
      }
    },
    [toast],
  );

  return { busy, download };
}

/**
 * Download any server-rendered file (calendar .ics, exports) through the same
 * object-URL dance, with a danger toast on failure and a `busy` flag for the
 * button. `fetcher` is one of the `api.*` methods that resolve `{blob, filename}`.
 */
export function useFileDownload(): {
  busy: boolean;
  download: (fetcher: () => Promise<{ blob: Blob; filename: string }>) => Promise<void>;
} {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const download = useCallback(async (fetcher: () => Promise<{ blob: Blob; filename: string }>) => {
    setBusy(true);
    try {
      const { blob, filename } = await fetcher();
      saveBlob(blob, filename);
    } catch (e) {
      toast.push(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  }, [toast]);
  return { busy, download };
}
